import React from 'react'
import { MoreHorizontal, MessageCircle, Calendar, AlertTriangle, TrendingUp, TrendingDown, Minus, Eye } from 'lucide-react'
import PerformanceLevelBadge from './PerformanceLevelBadge'
import CircularProgress from '../CircularProgress'

interface TeamMemberCardProps {
  id: string
  name: string
  role: string
  avatar?: string
  score: number
  previousScore?: number
  level: string
  trend: 'up' | 'down' | 'neutral'
  keyMetric?: {
    label: string
    value: string
    color: string
  }
  alertStatus?: 'none' | 'attention' | 'urgent'
  lastUpdated: string
  onActionClick?: (action: string, memberId: string) => void
}

const TeamMemberCard = ({ 
  id,
  name, 
  role, 
  avatar, 
  score, 
  previousScore,
  level, 
  trend,
  keyMetric,
  alertStatus = 'none',
  lastUpdated,
  onActionClick
}: TeamMemberCardProps) => {
  
  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return TrendingUp
      case 'down': return TrendingDown
      default: return Minus
    }
  }

  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-success'
      case 'down': return 'text-error'
      default: return 'text-brand-gray'
    }
  }

  const getAlertIcon = () => {
    if (alertStatus === 'urgent') return ''
    if (alertStatus === 'attention') return ''
    return null
  }

  const TrendIcon = getTrendIcon()

  return (
    <div className="card-hover p-6 group relative">
      {/* Alert Indicator */}
      {alertStatus !== 'none' && (
        <div className="absolute -top-2 -right-2">
          <div className={`p-1 rounded-full text-xs ${
            alertStatus === 'urgent' ? 'bg-error' : 'bg-warning'
          } text-white`}>
            {getAlertIcon()}
          </div>
        </div>
      )}

      {/* Header with Avatar and Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {avatar ? (
            <img 
              src={avatar} 
              alt={name}
              className="w-10 h-10 rounded-full border-2 border-brand-primary/20"
            />
          ) : (
            <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <span className="text-brand-primary font-display font-medium text-sm">
                {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-display font-semibold text-white group-hover:text-brand-primary transition-colors">
              {name}
            </h3>
            <p className="text-sm text-brand-gray">{role}</p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <button
            className="btn-icon border border-brand-gray/30 hover:border-success/50 hover:bg-success/10 text-brand-gray hover:text-success"
            onClick={() => onActionClick?.('view_details', id)}
            title="Ver detalhes"
            aria-label="Ver detalhes"
          >
            <Eye className="w-4 h-4" />
          </button>
          {/* HIDDEN: Schedule 1:1 button
          <button
            className="btn-icon border border-brand-gray/30 hover:border-brand-blue/50 hover:bg-brand-blue/10 text-brand-gray hover:text-brand-blue"
            onClick={() => onActionClick?.('schedule-1on1', id)}
            title="Agendar 1:1"
            aria-label="Agendar reunião 1:1"
          >
            <Calendar className="w-4 h-4" />
          </button>
          */}
          <button
            className="btn-icon border border-brand-gray/30 hover:border-brand-primary/50 hover:bg-brand-primary/10 text-brand-gray hover:text-brand-primary"
            onClick={() => onActionClick?.('send-feedback', id)}
            title="Enviar feedback"
            aria-label="Enviar feedback"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
          <button
            className="btn-icon border border-brand-gray/30 hover:border-brand-gray-light hover:bg-brand-dark-lighter text-brand-gray hover:text-white"
            onClick={() => onActionClick?.('more-options', id)}
            title="Mais opções"
            aria-label="Mais opções"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Performance Score and Level */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <CircularProgress percentage={score} size={60} />
          <div>
            <div className="text-2xl font-display font-bold text-brand-primary">
              {score}
            </div>
            <div className={`flex items-center space-x-1 text-sm font-medium ${getTrendColor()}`}>
              <TrendIcon size={14} />
              <span>
                {previousScore ? 
                  `${trend === 'up' ? '+' : trend === 'down' ? '-' : ''}${Math.abs(score - previousScore).toFixed(1)}` 
                  : 'N/A'
                }
              </span>
            </div>
          </div>
        </div>
        
        <PerformanceLevelBadge level={level} score={score} size="sm" />
      </div>

      {/* Key Metric */}
      {keyMetric && (
        <div className="mb-4">
          <div className="bg-brand-dark-lighter rounded-monumenta p-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-brand-gray font-display">
                {keyMetric.label}
              </span>
              <span className={`text-sm font-display font-bold ${keyMetric.color}`}>
                {keyMetric.value}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Last Updated */}
      <div className="flex items-center justify-between text-xs text-brand-gray border-t border-brand-gray/20 pt-3">
        <span>Atualizado: {lastUpdated}</span>
        {alertStatus !== 'none' && (
          <button 
            className="text-brand-primary hover:text-brand-primary/80 font-medium"
            onClick={() => onActionClick?.('view-alerts', id)}
          >
            Ver alertas
          </button>
        )}
      </div>
    </div>
  )
}

export default TeamMemberCard
